import type { QuizQuestionMedia, QuizQuestionMediaKind, QuizQuestionMediaSource } from '../types';

/**
 * Bundled quiz media are committed under `public/assets/quiz/` and served by Pages at
 * `/assets/quiz/{filename}` — e.g. `public/assets/quiz/intro-bell.mp3` → `/assets/quiz/intro-bell.mp3`.
 */
export const QUIZ_STATIC_ASSET_PREFIX = '/assets/quiz/';

const STATIC_ASSET_NAME_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,127}$/;

export function resolveQuizMediaUrl(media: QuizQuestionMedia): string {
  if (media.source === 'url') return media.value;
  const name = media.value.replace(/^\/+/, '');
  return `${QUIZ_STATIC_ASSET_PREFIX}${name}`;
}

/** Resolve a full URL, app path, or bundled filename under `public/assets/quiz/`. */
export function resolveBundledQuizAssetUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('/')
  ) {
    return trimmed;
  }
  return `${QUIZ_STATIC_ASSET_PREFIX}${trimmed.replace(/^\/+/, '')}`;
}

export function validateSponsorLogoUrl(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('/')) {
    if (trimmed.includes('..')) return 'Invalid sponsor logo path';
    return null;
  }

  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return 'Sponsor logo URL must use http or https';
    }
    return null;
  } catch {
    if (trimmed.includes('/') || trimmed.includes('\\') || trimmed.includes('..')) {
      return 'Static asset name must be a filename only';
    }
    if (!STATIC_ASSET_NAME_PATTERN.test(trimmed)) {
      return 'Invalid static asset name';
    }
    return null;
  }
}

export function parseQuestionMedia(
  kind: QuizQuestionMediaKind | null | undefined,
  source: QuizQuestionMediaSource | null | undefined,
  value: string | null | undefined,
): QuizQuestionMedia | null {
  if (!kind || !source || !value?.trim()) return null;
  return { kind, source, value: value.trim() };
}

export function validateQuizQuestionMedia(media: QuizQuestionMedia | null | undefined): string | null {
  if (!media) return null;

  const { kind, source, value } = media;
  if (kind !== 'image' && kind !== 'audio' && kind !== 'video') {
    return 'Invalid media kind';
  }
  if (source !== 'url' && source !== 'static') {
    return 'Invalid media source';
  }

  const trimmed = value?.trim();
  if (!trimmed) return 'Media value required when kind is set';

  if (source === 'static') {
    if (trimmed.includes('/') || trimmed.includes('\\') || trimmed.includes('..')) {
      return 'Static asset name must be a filename only';
    }
    if (!STATIC_ASSET_NAME_PATTERN.test(trimmed)) {
      return 'Invalid static asset name';
    }
    return null;
  }

  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return 'Media URL must use http or https';
    }
  } catch {
    return 'Invalid media URL';
  }

  if (trimmed.length > 2048) return 'Media URL too long';
  return null;
}

export function normalizeQuizQuestionMedia(
  media: QuizQuestionMedia | null | undefined,
): QuizQuestionMedia | null {
  if (!media?.kind || !media.source) return null;
  const trimmed = media.value?.trim();
  if (!trimmed) return null;
  return { kind: media.kind, source: media.source, value: trimmed };
}
